import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { ApiError } from '../../../../services/api';
import {
  listScholarships,
  transferScholarshipPlayer,
} from '../../../../services/scholarships';
import type {
  ScholarshipListItem,
  ScholarshipPlayer,
} from '../../../../types/scholarships';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import './transferPlayer.css';

interface Props {
  /** The recruiting board the player is committing on. */
  scholarshipId: string;
  player: ScholarshipPlayer;
  onClose: () => void;
  /**
   * The list's version at the moment the move went out, captured for the parent so it can tell a
   * reply that still describes its list from one that has been overtaken.
   */
  listVersion: () => number;
  /**
   * The source board's remaining players, straight from the transfer response, with the version
   * captured before the request went out.
   *
   * Fired whenever the move LANDS, including when the coach dismissed this dialog while it was in
   * flight -- see handleConfirm. The parent owns the list, so it is the one that has to hear about
   * a player who is no longer on the board, and the one that decides whether this whole-list reply
   * is still current enough to render.
   */
  onTransferred: (remaining: ScholarshipPlayer[], since: number) => void;
}

const ALREADY_THERE =
  'That player is already on that roster, so there is nothing to move. Close this and remove them from the board here if you want to.';

const NO_TARGET_PICKED = 'Pick the roster to move them to first.';

/** The API's own name for "this player is already on the board you are moving them to". */
const ALREADY_ON_TARGET_CODE = 'AlreadyOnTargetBoard';

/**
 * Matched on the body's name for the conflict, not on 409 alone. A status shared by every conflict
 * the API might add later would quietly read as "already on that roster" and tell the coach
 * something untrue.
 *
 * Both spellings are read, exactly as mobile's isAlreadyOnTargetBoard does. The controller sends
 * `error`, which is what the API's own test asserts; the contract for the endpoint calls it a
 * code. `ApiError.response` is `unknown`, so nothing here is checked by the compiler -- the
 * fixtures in the tests are the only guard, and they carry the body the controller really sends.
 */
const isAlreadyThere = (e: unknown): boolean => {
  if (!(e instanceof ApiError) || e.statusCode !== 409) return false;
  const body = e.response as { code?: unknown; error?: unknown } | null | undefined;
  return body?.code === ALREADY_ON_TARGET_CODE || body?.error === ALREADY_ON_TARGET_CODE;
};

/**
 * The roster management boards a player can be moved to. `failed` is kept apart from an empty
 * list on purpose: a request that fell over is not the same answer as "you have not made one
 * yet", and telling a coach to go and create a board they already have sends them off to fix
 * nothing.
 */
type BoardsState =
  /**
   * `previousError` is the message of the load this one is retrying. It keeps the alert -- and
   * with it the Try again button the coach just pressed -- on screen while the retry runs. An
   * unmounting button drops focus to <body>, outside an aria-modal dialog.
   */
  | { status: 'loading'; previousError?: string }
  | { status: 'ready'; boards: ScholarshipListItem[] }
  | { status: 'failed'; message: string };

/**
 * Where a committed recruit goes next.
 *
 * Offered after a coach ticks Committed on a recruiting board, and **only offered**: committing
 * and moving a player onto a roster are two decisions, and a coach may well want the first
 * without the second yet. Dismissing this leaves the commitment exactly as it was saved.
 */
export const TransferPlayerModal = ({
  scholarshipId,
  player,
  onClose,
  listVersion,
  onTransferred,
}: Props) => {
  const groupId = useId();
  const [boardsState, setBoardsState] = useState<BoardsState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [targetId, setTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The coach walked out of the dialog mid-flight. Only worth knowing for the FAILURE path: there
  // is nothing on screen left to show the reason on. A success still has to be reported, because
  // the request is not abortable and the server moves the player either way.
  const dismissedRef = useRef(false);

  /*
    The dialog ConfirmModal renders around this, found through the retry button while it is still
    mounted. A retry that SUCCEEDS takes the button away with it, and focus would land on <body>
    outside the dialog, so it is put back on the dialog itself.
  */
  const dialogRef = useRef<HTMLElement | null>(null);
  const retried = useRef(false);

  const loadBoards = useCallback(() => {
    retried.current = true;
    setBoardsState((current) =>
      current.status === 'failed'
        ? { status: 'loading', previousError: current.message }
        : { status: 'loading' },
    );
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (boardsState.status === 'loading' || !retried.current) return;
    retried.current = false;
    // Only when the press really did strand it. A coach who tabbed elsewhere meanwhile keeps
    // where they are.
    if (document.activeElement === document.body) dialogRef.current?.focus();
  }, [boardsState.status]);

  useEffect(() => {
    let live = true;
    listScholarships(false)
      .then((all) => {
        if (!live) return;
        /*
          Roster management boards only, and never the board being worked on. "Transfer to another
          recruiting board" is a different request from this one, and the point here is getting a
          committed player onto the roster they have committed to.
        */
        setBoardsState({
          status: 'ready',
          boards: all.filter((s) => s.purpose !== 'Recruiting' && s.id !== scholarshipId),
        });
      })
      .catch((e) => {
        if (!live) return;
        setBoardsState({
          status: 'failed',
          message: getSafeErrorMessage(e, 'Failed to load your rosters'),
        });
      });
    return () => {
      live = false;
    };
  }, [scholarshipId, attempt]);

  const handleClose = () => {
    dismissedRef.current = true;
    onClose();
  };

  const handleConfirm = async () => {
    if (transferring) return;
    if (targetId === '') {
      // Said out loud rather than shown as a dead button. The confirm is only marked busy while
      // something is actually in flight, so "nothing picked yet" needs somewhere to be read.
      setError(NO_TARGET_PICKED);
      return;
    }
    setTransferring(true);
    setError(null);
    // Captured before the request goes out: the parent applies this reply only if nothing has
    // changed its list meanwhile.
    const since = listVersion();
    try {
      const remaining = await transferScholarshipPlayer(scholarshipId, player.id, targetId);
      /*
        Reported even if the coach pressed "Not now" while this was out. ConfirmModal keeps
        dismissal live on purpose (a request that never settles must not trap anyone), and the
        POST is not abortable, so the player HAS moved. Skipping this left the source board
        rendering a row that no longer exists: its counts were wrong and the next edit of it 404'd.
        The dismissal stops the offer, not the follow-through.
      */
      onTransferred(remaining, since);
    } catch (e) {
      if (dismissedRef.current) return;
      setError(isAlreadyThere(e) ? ALREADY_THERE : getSafeErrorMessage(e, 'Failed to move the player'));
      setTransferring(false);
    }
  };

  const name = `${player.firstName} ${player.lastName}`.trim();
  const boards = boardsState.status === 'ready' ? boardsState.boards : [];

  return (
    <ConfirmModal
      isOpen
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={`Move ${name} to a roster?`}
      message={`${name} is committed. You can move them onto one of your roster management boards now, or leave them here and do it later.`}
      confirmText={transferring ? 'Moving...' : 'Move player'}
      cancelText="Not now"
      // The move, and nothing else. Marking the button busy while the coach simply has not picked
      // a roster yet announced `aria-busy` with nothing in flight to be busy with.
      busy={transferring}
    >
      {boardsState.status === 'loading' && !boardsState.previousError && (
        <p className="transfer-player__status">Loading your rosters...</p>
      )}

      {/*
        The alert and its button stay put while a retry runs, with the button marked busy rather
        than taken away. Unmounting the control the coach just pressed drops focus to <body>,
        outside this aria-modal dialog, and the plain paragraph that replaced it announced nothing.
      */}
      {(boardsState.status === 'failed' ||
        (boardsState.status === 'loading' && boardsState.previousError)) && (
        <div className="scholarship-modal__error" role="alert">
          {boardsState.status === 'failed' ? boardsState.message : boardsState.previousError}
          <div>
            <button
              type="button"
              className="transfer-player__retry"
              ref={(node) => {
                if (node) dialogRef.current = node.closest('[role="dialog"]');
              }}
              // aria-disabled, not disabled: see ConfirmModal's confirm button. A real disabled
              // strands a keyboard on the press that starts the retry.
              aria-disabled={boardsState.status === 'loading'}
              aria-busy={boardsState.status === 'loading'}
              onClick={() => {
                if (boardsState.status === 'loading') return;
                loadBoards();
              }}
            >
              {boardsState.status === 'loading' ? 'Trying again...' : 'Try again'}
            </button>
          </div>
        </div>
      )}

      {boardsState.status === 'ready' && boards.length === 0 && (
        <p className="transfer-player__status">
          You do not have a roster management board to move them to yet. Create one from Roster
          Planning, then come back.
        </p>
      )}

      {boards.length > 0 && (
        /*
          A radio list rather than a dropdown. There is usually a handful of rosters, the whole
          choice fits on screen, and nothing starts selected -- the coach picks the roster, so
          Move player cannot land on whichever board happened to be first.
        */
        <div className="transfer-player__group" role="group" aria-labelledby={`${groupId}-label`}>
          <span id={`${groupId}-label`} className="transfer-player__group-label">
            Move to
          </span>
          {boards.map((b) => (
            <div key={b.id} className="transfer-player__option">
              <input
                type="radio"
                id={`${groupId}-${b.id}`}
                name={groupId}
                value={b.id}
                checked={targetId === b.id}
                disabled={transferring}
                onChange={() => {
                  setTargetId(b.id);
                  // Clears "pick a roster first" the moment they do.
                  setError((current) => (current === NO_TARGET_PICKED ? null : current));
                }}
              />
              <label htmlFor={`${groupId}-${b.id}`}>{b.name}</label>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="scholarship-modal__error" role="alert">
          {error}
        </div>
      )}
    </ConfirmModal>
  );
};
